<template>
  <div class="p-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0">CF Enterprise Bulk Automation</h5>
      <button class="btn btn-primary" :disabled="false ||batchCreating || hasActiveBatch" @click="initiateBulkEnterprise">
        <i class="fa fa-bolt mr-1"></i>
        <span v-if="batchCreating"><i class="fa fa-spinner fa-spin mr-1"></i> Creating Batch...</span>
        <span v-else>Initiate CF Enterprise in Bulk</span>
      </button>
    </div>
    <p class="text-muted mb-3">Select how many pending CF Enterprise tasks to queue into a new automation batch.</p>
    <div v-if="hasActiveBatch" class="alert mb-4" style="background:#fff8e1;border:1px solid #ffe082;border-left:4px solid #f9a825;border-radius:6px;padding:12px 16px;display:flex;align-items:center;gap:10px;">
      <i class="fa fa-exclamation-triangle" style="color:#f9a825;font-size:18px;flex-shrink:0;"></i>
      <div>
        <strong style="color:#7a5c00;">Batch In Progress</strong>
        <div style="font-size:13px;color:#8a6d00;margin-top:2px;">A batch is currently active. You cannot create a new batch until the existing one is complete or failed.</div>
      </div>
    </div>

    <div v-if="loadingBatches" class="text-center py-4">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
    </div>
    <div v-else-if="batches.length === 0" class="alert alert-light text-muted text-center">
      No batches created yet.
    </div>
    <div v-else class="table-responsive">
      <table class="table table-bordered table-hover">
        <thead class="thead-light">
          <tr>
            <th>Batch ID</th>
            <th>Status</th>
            <th>Domains</th>
            <th>Created At</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="batch in batches" :key="batch.id">
            <td style="font-family:monospace;font-size:12px;">{{ batch.batchId }}</td>
            <td>
              <span class="badge" :class="statusBadge(batch.status)">{{ statusLabel(batch.status) }}</span>
            </td>
            <td>{{ batch.totalCount }}</td>
            <td>{{ formatDate(batch.createdAt) }}</td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-secondary" @click="$router.push({ name: 'cfEnterpriseBatchDetail', params: { batchId: batch.batchId } })">
                <i class="fa fa-list mr-1"></i> View Domains
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {db} from '@/firebase/init';
import swal from 'sweetalert2';

export default {
  name: 'CFProvisioning',
  data() {
    return {
      batchCreating: false,
      loadingBatches: false,
      batches: [],
      unsubscribe: null
    };
  },
  mounted() {
    this.listenBatches();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  computed: {
    hasActiveBatch() {
      const done = ['complete', 'completed', 'failed', 'fail'];
      return this.batches.some(b => !done.includes(b.status));
    }
  },
  methods: {
    listenBatches() {
      this.loadingBatches = true;
      this.unsubscribe = db.collection('cfEnterpriseBatches')
        .orderBy('createdAt', 'desc')
        .onSnapshot(snapshot => {
          this.batches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          this.loadingBatches = false;
        }, err => {
          console.error('listenBatches error', err);
          this.loadingBatches = false;
        });
    },
    statusBadge(status) {
      return {
        'badge-warning': status === 'pending',
        'badge-info': status === 'running' || status === 'in-progress' || status === 'inProgress',
        'badge-success': status === 'complete' || status === 'completed',
        'badge-danger': status === 'failed' || status === 'fail'
      };
    },
    statusLabel(status) {
      const map = {
        'pending': 'Pending',
        'running': 'Running',
        'in-progress': 'In Progress',
        'inProgress': 'In Progress',
        'complete': 'Complete',
        'completed': 'Complete',
        'failed': 'Failed',
        'fail': 'Failed'
      };
      return map[status] || status;
    },
    formatDate(val) {
      if (!val) return '—';
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleString();
    },
    async initiateBulkEnterprise() {
      const result = await swal({
        title: 'Bulk CF Enterprise Automation',
        html: `
          <p class="mb-2">How many pending tasks do you want to automate?</p>
          <input id="swal-batch-count" type="number" min="1" max="100" value="5"
            class="swal2-input" style="width:120px;text-align:center;" />
        `,
        showCancelButton: true,
        confirmButtonText: 'Create Batch',
        preConfirm: () => {
          const val = parseInt(document.getElementById('swal-batch-count').value, 10);
          if (!val || val < 1 || val > 100) {
            swal.showValidationMessage('Please enter a number between 1 and 100');
            return false;
          }
          return val;
        }
      });

      const count = result.value;
      if (!count) return;

      try {
        this.batchCreating = true;

        const domains = [];
        let lastDoc = null;
        while (domains.length < count) {
          let query = db.collection('domains')
            .where('cfEnterprise.status', '==', 'pending')
            .limit(count * 2);

          if (lastDoc) query = query.startAfter(lastDoc);

          const snapshot = await query.get();
          if (snapshot.empty) break;

          for (const doc of snapshot.docs) {
            const cf = doc.data().cfEnterprise || {};
            if (!cf.checkinBy) {
              domains.push({ ip: doc.data().ip || '', id: doc.id, uid: doc.data().uid || '', domain: doc.data().domain || '', status: 'pending' });
              if (domains.length === count) break;
            }
          }

          lastDoc = snapshot.docs[snapshot.docs.length - 1];
          if (snapshot.docs.length < count * 2) break;
        }

        if (!domains.length) {
          return swal('No Pending Tasks', 'There are no pending CF Enterprise tasks without an assigned agent to queue.', 'info');
        }

        const batchRef = db.collection('cfEnterpriseBatches').doc();
        await batchRef.set({
          batchId: batchRef.id,
          status: 'pending',
          createdAt: new Date(),
          totalCount: domains.length,
          domains
        });

        await this.$http.post('zone/batch-automate/run', {
          grant_type: 'token',
          batchId: batchRef.id
        });

        // swal('Batch Created', `Successfully queued ${domains.length} domain${domains.length !== 1 ? 's' : ''} into a new batch.`, 'success');
      } catch (e) {
        console.error('initiateBulkEnterprise error', e);
        swal('Error', 'Something went wrong while creating the batch.', 'error');
      } finally {
        this.batchCreating = false;
      }
    }
  }
};
</script>
